import { useQuery } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import LinkTag from "common/LinkTag";
import Table from "common/Table";
import RenderStatus from "common/Table/renderCell/RenderStatus";
import Toaster from "common/Toaster";
import EditButton from "common/detailElement/EditButton";
import { GET_ALL_INVOICE_RECEIPT } from "./graphql/query";
import moment from "moment";
import React from "react";
import { useState } from "react";
import { INITIAL_DATA_TABLE_LIMIT, getFilteredValue } from "utils";
import FilterInput from "common/FilterElement/FilterInput";
import FilterContainer from "common/FilterElement/FilterContainer";
import { useFilterForm } from "common/FilterDrawer/FilterForm";
import FilterClearButton from "common/FilterElement/FilterClearButton";
import useDebounce from "utils/useDebounce";

const columns = [
  {
    field: "id",
    headerName: "ID",
    flex: 0.4,
    renderCell: ({ row }) => {
      return <LinkTag to={row.objectId}>{row.objectId}</LinkTag>;
    },
  },
  {
    field: "customer__user__name",
    headerName: "Member",
    flex: 0.8,
    renderCell: ({ row }) => {
      return (
        <LinkTag
          to={`/admin/members/${row?.customer?.user?.memberInfo?.objectId}`}
        >
          {row?.customer?.user?.name}
        </LinkTag>
      );
    },
  },
  {
    field: "packages",
    headerName: "Package",
    flex: 0.8,
    sortable: false,
    renderCell: ({ row }) => {
      let packageBooked = row?.packages?.edges?.[0]?.node;
      return (
        <>
          {packageBooked?.membershipPackage ? (
            <LinkTag
              to={`/admin/membershipPackages/${packageBooked?.membershipPackage?.package?.objectId}`}
            >
              {packageBooked?.membershipPackage?.package?.name}
            </LinkTag>
          ) : null}
          {packageBooked?.dietPlanPackage ? (
            <LinkTag
              to={`/admin/dietPlanPackages/${packageBooked?.dietPlanPackage?.package?.objectId}`}
            >
              {packageBooked?.dietPlanPackage?.package?.name}
            </LinkTag>
          ) : null}
          {packageBooked?.personalTrainingPackage ? (
            <LinkTag
              to={`/admin/personalTrainingPackage/${packageBooked?.personalTrainingPackage?.package?.objectId}`}
            >
              {packageBooked?.personalTrainingPackage?.package?.name}
            </LinkTag>
          ) : null}
        </>
      );
    },
  },
  {
    field: "paymentType",
    headerName: "Payment Type",
    flex: 0.5,
  },

  {
    field: "paidAmount",
    headerName: "Paid Amount",
    flex: 0.5,
  },
  {
    field: "balance",
    headerName: "Balance",
    flex: 0.4,
    sortable: false,
  },
  {
    field: "deliveryReport",
    headerName: "Email Delivery Status",
    flex: 0.5,
    minWidth: 150,
    renderCell: ({ value }) => {
      return (
        <>
          {value && JSON.parse(value) ? (
            <RenderStatus
              value={
                JSON.parse(value)?.delivered ? "delivered" : "not-delivered"
              }
            />
          ) : (
            <RenderStatus value={"not-delivered"} />
          )}
        </>
      );
    },
  },
  {
    field: "createdOn",
    headerName: "Created On",
    flex: 0.5,
    renderCell: ({ row }) => {
      return <>{moment(row.createdOn).format("DD MMM YYYY")}</>;
    },
  },
  {
    field: "action",
    headerName: "Action",
    flex: 0.65,
    sortable: false,
    renderCell: ({ row }) => {
      let packageBooked = row?.packages?.edges?.[0]?.node;

      let packageOf = packageBooked?.membershipPackage
        ? "membershipPackageBookings"
        : packageBooked?.dietPlanPackage
        ? "nutritionPlanPackageBookings"
        : packageBooked?.personalTrainingPackage
        ? "personalTrainingPackageBookings"
        : "";

      return (
        <LinkTag to={`/admin/${packageOf}/invoiceReceipt/${row.objectId}`}>
          <EditButton label="Receipt" />
        </LinkTag>
      );
    },
  },
];

const INITIAL_STATE = {
  user: { type: "text", value: "" },
};

const InvoiceReceipt = () => {
  const [rows, setRows] = useState([]);
  const { filter, setFilter } = useFilterForm(INITIAL_STATE);

  const { loading, data, fetchMore } = useQuery(GET_ALL_INVOICE_RECEIPT, {
    variables: {
      first: INITIAL_DATA_TABLE_LIMIT,
      orderBy: "-createdOn",
    },
    onError: (error) => {
      Toaster({
        message: error.message,
        type: "error",
      });
    },
    onCompleted: (res) => {
      let tempRes = res?.invoiceReceipts?.edges?.map((item) => item.node);
      setRows(tempRes);
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const debounceFunction = useDebounce((value) => fetchMoreData(value), 500);

  const onClearFilter = () => {
    setFilter(INITIAL_STATE);

    fetchMore({
      variables: {
        first: INITIAL_DATA_TABLE_LIMIT,
      },
    });
  };

  const onChangeDebounce = (e) => {
    let name = e.target.name;
    let value = e.target.value;
    if (name === "type" && value === "") {
      onClearFilter();
      return;
    }

    let key = name;
    setFilter((state) => ({
      ...filter,
      [key]: { ...state[key], value: value },
    }));

    debounceFunction(
      getFilteredValue({ ...filter, [key]: { ...filter[key], value: value } })
    );
  };

  const fetchMoreData = async (variables) => {
    await fetchMore({
      variables: variables,
    });
  };

  return (
    <Box>
      <Typography variant="h6" mb={2} color="#0a0a0a">
        Invoice Receipt
      </Typography>
      <FilterContainer>
        <FilterInput
          label="Member"
          name="user"
          value={filter["user"].value}
          onChange={onChangeDebounce}
        />

        <FilterClearButton onClick={onClearFilter} />
      </FilterContainer>

      <Table
        columns={columns}
        rows={rows}
        loading={loading}
        fetchMore={fetchMore}
        totalCount={data?.invoiceReceipts?.totalCount}
        rowHeight={70}
      />
    </Box>
  );
};

export default InvoiceReceipt;
